/**
 * Sample React Native App
 * https://github.com/facebook/react-native
 *
 * @format
 */

import React, {useEffect, useLayoutEffect, useState} from 'react';
import {Platform, View, Text} from 'react-native';
import {GestureHandlerRootView} from 'react-native-gesture-handler';
import {SafeAreaProvider} from 'react-native-safe-area-context';
// Defer potentially brittle imports to diagnose upgrade issues gracefully
let MenuProvider = ({children}) => children;
let Toast = () => null;
try {
  // eslint-disable-next-line @typescript-eslint/no-var-requires
  MenuProvider = require('react-native-popup-menu').MenuProvider;
} catch (e) {
  // eslint-disable-next-line no-console
  console.error('Failed to import react-native-popup-menu:', e);
}
try {
  // eslint-disable-next-line @typescript-eslint/no-var-requires
  Toast = require('react-native-toast-message');
  Toast = Toast?.default || Toast;
} catch (e) {
  // eslint-disable-next-line no-console
  console.error('Failed to import react-native-toast-message:', e);
  Toast = () => null;
}

// Defer AppNavigation import to isolate first-failure module during upgrade
let AppNavigation = null;
import AccountStatusContext from './src/contexts/AccountStatusContext';
import LoadingPauseContext from './src/contexts/LoadingPauseContext';
import ErrorStatusContext from './src/contexts/ErrorStatusContext';
import ShopContext from './src/contexts/ShopContext';
import BLESyncContext from './src/contexts/BLESyncContext';

import {configureAxios} from './src/utils/ApiHelper';
import {purgeSecureStoreOnFreshInstall} from './src/utils/SecureStore';
import {setupAnalytics} from './src/utils/Analytics';
import {toastConfig} from './src/utils/ToastConfig';
import EligibilityCheckContext from './src/contexts/EligibilityCheckContext';
import {ThemedStatusBar, ThemeProvider} from './src/theme';

const App = () => {
  const [takeToLogin, setTakeToLogin] = useState(false); //the way appnavigation is setup, this variable only gets sent to the web component

  const triggerLogin = () => {
    setTakeToLogin(true);
  };

  useEffect(() => {
    if (Platform.OS === 'web') setupAnalytics();
    // Keychain values survive uninstall; drop them on the first run of a
    // fresh install so no session/PHI outlives the app.
    purgeSecureStoreOnFreshInstall();
  }, []);

  useEffect(() => {
    if (takeToLogin) {
      //this is required so we dont end up in a infinite loop
      setTakeToLogin(false);
    }
  }, [takeToLogin]); //runs everytime the value of takeToLogin updates

  useLayoutEffect(() => {
    configureAxios(triggerLogin); //Axios is responsible for triggering login. Only web axios accepts this parameter
  }, []);

  // Lazy-resolve AppNavigation to surface the first import that fails
  let NavigationEl = null;
  try {
    const mod = require('./src/AppNavigation');
    AppNavigation = mod?.default ?? mod;
    NavigationEl = <AppNavigation takeToLogin={takeToLogin} />;
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('Failed to import AppNavigation:', e);
    NavigationEl = (
      <View style={{flex: 1, alignItems: 'center', justifyContent: 'center'}}>
        <Text>Failed to import AppNavigation. See Metro logs.</Text>
      </View>
    );
  }

  return (
    <SafeAreaProvider>
      {/* ThemeProvider sits above every UI provider so menus/toasts can
          consume theme tokens as their tranches migrate (docs/DARK-MODE.md) */}
      <ThemeProvider>
        <MenuProvider customStyles={{optionsContainer: {borderRadius: 16, paddingHorizontal: 16}}}>
          <GestureHandlerRootView style={{flex: 1}}>
            <ThemedStatusBar />
            <LoadingPauseContext>
              <ErrorStatusContext>
                <ShopContext>
                  <AccountStatusContext>
                    <EligibilityCheckContext>
                      <BLESyncContext>
                        {NavigationEl}
                        <Toast config={toastConfig} />
                      </BLESyncContext>
                    </EligibilityCheckContext>
                  </AccountStatusContext>
                </ShopContext>
              </ErrorStatusContext>
            </LoadingPauseContext>
          </GestureHandlerRootView>
        </MenuProvider>
      </ThemeProvider>
    </SafeAreaProvider>
  );
};

export default App;
